if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      static nextId = 0;

      constructor() {
        super();
        this.instanceId = ++this.constructor.nextId;
        this.requestVersion = 0;
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
      }

      connectedCallback() {
        this.errorHtml ||= this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        if (this.hasAttribute('available')) this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.cancelRequest();
        this.clearDrawer();
      }

      cancelRequest() {
        this.requestVersion++;
        this.abortController?.abort();
      }

      clearDrawer() {
        this.drawer?.remove();
        this.drawer = null;
      }

      fetchAvailability(variantId) {
        if (!variantId || !this.isConnected) return;
        this.cancelRequest();
        if (this.dataset.variantId !== String(variantId)) {
          this.clearDrawer();
          this.innerHTML = '';
          this.removeAttribute('available');
        }
        this.dataset.variantId = String(variantId);
        const version = this.requestVersion;
        const isCurrent = () => this.isConnected && version === this.requestVersion;
        this.abortController = new AbortController();

        let rootUrl = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Pickup availability request failed');
            return response.text();
          })
          .then((text) => {
            if (!isCurrent()) return;
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            if (!sectionInnerHTML) throw new Error('Pickup availability section missing');
            this.renderPreview(sectionInnerHTML);
          })
          .catch(() => {
            if (!isCurrent()) return;
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.cancelRequest();
          this.clearDrawer();
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.clearDrawer();
        this.innerHTML = '';
        if (!this.errorHtml) return;
        this.appendChild(this.errorHtml.cloneNode(true));
        this.setAttribute('available', '');
        this.querySelector('button')?.addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        this.clearDrawer();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        const drawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
        if (!drawer) throw new Error('Pickup availability drawer missing');
        const headingId = drawer.getAttribute('aria-labelledby');
        const heading = headingId && drawer.querySelector(`[id="${headingId}"]`);
        if (heading) {
          heading.id = `${headingId}-${this.instanceId}`;
          drawer.setAttribute('aria-labelledby', heading.id);
        }
        this.drawer = drawer;
        document.body.appendChild(drawer);
        const colorClassesToApply = (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean);
        colorClassesToApply.forEach((colorClass) => {
          drawer.classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button) {
          if (button.id) button.id = `${button.id}-${this.instanceId}`;
          button.addEventListener('click', (evt) => {
            drawer.show(evt.currentTarget);
          });
        }
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          !this.focusElement?.contains(target)
        ) {
          this.hide();
        }
      }

      disconnectedCallback() {
        if (this.hasAttribute('open')) this.hide();
        document.body.removeEventListener('click', this.onBodyClick);
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        document.querySelectorAll('pickup-availability-drawer[open]').forEach((drawer) => {
          if (drawer !== this) drawer.hide();
        });
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
